import { BASE_URL, API_ENDPOINTS } from '../config/apiconfig';
import api from '../api/axiosInstance';

/**
 * App Preloader Service
 * Preloads essential data after login to improve user experience
 */

class AppPreloader {
  constructor() {
    this.cache = new Map();
    this.loadingPromises = new Map();
    this.CACHE_KEY = 'app_preloader_cache';
    this.CACHE_EXPIRY_KEY = 'app_preloader_cache_expiry';
    this.CACHE_DURATION = 15 * 60 * 1000; // 15 minutes
    
    // Load cache from localStorage on initialization
    this.loadFromLocalStorage();
  }

  /**
   * Load cached data from localStorage
   */
  loadFromLocalStorage() {
    try {
      const cachedData = localStorage.getItem(this.CACHE_KEY);
      const cacheExpiry = localStorage.getItem(this.CACHE_EXPIRY_KEY);
      
      if (cachedData && cacheExpiry) {
        const expiryTime = parseInt(cacheExpiry);
        const currentTime = Date.now();
        
        // Check if cache is still valid
        if (currentTime < expiryTime) {
          const parsedCache = JSON.parse(cachedData);
          Object.entries(parsedCache).forEach(([key, value]) => {
            this.cache.set(key, value);
          });
          console.log('Loaded cached data from localStorage:', Object.keys(parsedCache));
        } else {
          // Cache expired, clear it
          this.clearLocalStorage();
        }
      }
    } catch (error) {
      console.error('Error loading cache from localStorage:', error);
      this.clearLocalStorage();
    }
  }

  /**
   * Save cache to localStorage
   */
  saveToLocalStorage() {
    try {
      const cacheObject = {};
      this.cache.forEach((value, key) => {
        cacheObject[key] = value;
      });
      
      localStorage.setItem(this.CACHE_KEY, JSON.stringify(cacheObject));
      localStorage.setItem(this.CACHE_EXPIRY_KEY, (Date.now() + this.CACHE_DURATION).toString());
    } catch (error) {
      console.error('Error saving cache to localStorage:', error);
    }
  }

  /**
   * Clear localStorage cache
   */
  clearLocalStorage() {
    try {
      localStorage.removeItem(this.CACHE_KEY);
      localStorage.removeItem(this.CACHE_EXPIRY_KEY);
    } catch (error) {
      console.error('Error clearing localStorage cache:', error);
    }
  }

  /**
   * Clear all cached data
   */
  clearCache() {
    this.cache.clear();
    this.loadingPromises.clear();
    this.clearLocalStorage();
  }

  /**
   * Get cached data or fetch if not available
   */
  async getOrFetch(key, fetchFn) {
    // Return cached data if available
    if (this.cache.has(key)) {
      return this.cache.get(key);
    }

    // Return existing promise if already loading
    if (this.loadingPromises.has(key)) {
      return this.loadingPromises.get(key);
    }

    // Create new fetch promise
    const promise = fetchFn()
      .then(data => {
        this.cache.set(key, data);
        this.loadingPromises.delete(key);
        // Save to localStorage after successful fetch
        this.saveToLocalStorage();
        return data;
      })
      .catch(error => {
        this.loadingPromises.delete(key);
        throw error;
      });

    this.loadingPromises.set(key, promise);
    return promise;
  }

  /**
   * Fetch profile data (business name)
   */
  async fetchProfile() {
    return this.getOrFetch('profile', async () => {
      const response = await api.get(`${API_ENDPOINTS.PROFILE.GET}/getById/1`);
      return response.data;
    });
  }

  /**
   * Fetch WhatsApp credits
   */
  async fetchWhatsappCredits() {
    return this.getOrFetch('whatsappCredits', async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        console.warn('No token available for WhatsApp credits fetch');
        return null;
      }

      const url = `${BASE_URL}${API_ENDPOINTS.WHATSAPP.GET_CREDITS}`;
      console.log('Fetching WhatsApp credits from:', url);
      
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      console.log('WhatsApp credits response status:', response.status);
      
      if (!response.ok) {
        console.error('Failed to fetch WhatsApp credits:', response.status, response.statusText);
        return null;
      }
      
      const data = await response.json();
      console.log('WhatsApp credits raw data:', data);
      console.log('WhatsApp credits data type:', typeof data);
      console.log('WhatsApp credits data keys:', data ? Object.keys(data) : 'null/undefined');
      
      // Handle different response structures
      // The API might return { credits: 100 } or just 100 directly
      const creditsValue = data?.credits || data?.value || data?.count || data;
      console.log('WhatsApp credits extracted value:', creditsValue);
      
      return creditsValue;
    });
  }

  /**
   * Fetch dashboard data
   */
  async fetchDashboardData() {
    return this.getOrFetch('dashboardData', async () => {
      const response = await api.get(API_ENDPOINTS.DASHBOARD.GET_DATA);
      return response.data;
    });
  }

  /**
   * Fetch customers list
   */
  async fetchCustomers() {
    return this.getOrFetch('customers', async () => {
      const response = await api.get(`${API_ENDPOINTS.CUSTOMERS.SEARCH}?page=0&size=1000`);
      return response.data;
    });
  }

  /**
   * Fetch goods list
   */
  async fetchGoods() {
    return this.getOrFetch('goods', async () => {
      const response = await api.get(API_ENDPOINTS.GOODS.GET_ALL);
      return response.data;
    });
  }

  /**
   * Fetch users list
   */
  async fetchUsers() {
    return this.getOrFetch('users', async () => {
      const response = await api.get(API_ENDPOINTS.USERS.GET_ALL);
      return response.data;
    });
  }

  /**
   * Preload all essential data
   * Called after successful login
   */
  async preloadAll() {
    console.log('Starting data preload...');
    
    // Clear cache before preloading to prevent showing previous user's data
    this.clearCache();
    console.log('Cache cleared before preload');
    
    const results = {
      profile: null,
      whatsappCredits: null,
      dashboardData: null,
      customers: null,
      goods: null,
      users: null,
      errors: [],
    };

    // Fetch all data in parallel for faster loading
    const promises = [
      this.fetchProfile().catch(error => {
        console.error('Failed to fetch profile:', error);
        results.errors.push({ key: 'profile', error });
        return null;
      }),
      this.fetchWhatsappCredits().catch(error => {
        console.error('Failed to fetch WhatsApp credits:', error);
        results.errors.push({ key: 'whatsappCredits', error });
        return null;
      }),
      this.fetchDashboardData().catch(error => {
        console.error('Failed to fetch dashboard data:', error);
        results.errors.push({ key: 'dashboardData', error });
        return null;
      }),
      this.fetchCustomers().catch(error => {
        console.error('Failed to fetch customers:', error);
        results.errors.push({ key: 'customers', error });
        return null;
      }),
      this.fetchGoods().catch(error => {
        console.error('Failed to fetch goods:', error);
        results.errors.push({ key: 'goods', error });
        return null;
      }),
      this.fetchUsers().catch(error => {
        console.error('Failed to fetch users:', error);
        results.errors.push({ key: 'users', error });
        return null;
      }),
    ];

    const [
      profile,
      whatsappCredits,
      dashboardData,
      customers,
      goods,
      users,
    ] = await Promise.all(promises);

    results.profile = profile;
    results.whatsappCredits = whatsappCredits;
    results.dashboardData = dashboardData;
    results.customers = customers;
    results.goods = goods;
    results.users = users;

    console.log('Preload results:', {
      profile: !!profile,
      whatsappCredits: !!whatsappCredits,
      dashboardData: !!dashboardData,
      customers: !!customers,
      goods: !!goods,
      users: !!users,
      errors: results.errors.length
    });

    return results;
  }

  /**
   * Get cached data by key
   */
  getCachedData(key) {
    return this.cache.get(key);
  }

  /**
   * Check if data is cached
   */
  hasCachedData(key) {
    return this.cache.has(key);
  }
}

// Export singleton instance
const appPreloader = new AppPreloader();
export default appPreloader;
